/**
 * Zendesk Messenger Web Widget helpers.
 *
 * Injects the `ze-snippet` script and authenticates via `loginUser` with a
 * server-minted chat JWT. The vendor launcher bubble is suppressed — TruSpace
 * mounts a custom FAB (nudgeable within a bottom-right pocket) that calls
 * {@link openZendeskMessenger}.
 *
 * On TruSpace logout / user switch, call {@link logoutZendeskUser} then
 * {@link loginZendeskUser} again so conversation history does not leak
 * across accounts.
 *
 * Circuit breaker: Messenger may re-invoke the JWT callback when a token is
 * rejected or when `loginUser` is re-registered after a React effect cleanup.
 * Without a hard cap that becomes a white-page request storm (Stage QA saw
 * 30k+ `/api/support/zendesk/chat-token` calls for DrawingVaultUser).
 *
 * @see https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/
 */

const ZENDESK_KEY = import.meta.env.VITE_ZENDESK_KEY as string | undefined;

/**
 * When true, mimic pre-fix Stage/RC11 Messenger wiring (no JWT circuit breaker,
 * effect cleanup calls logoutUser) so QA can reproduce the chat-token storm locally.
 * NEVER enable in production builds.
 */
export function isZendeskStageReplica(): boolean {
  return import.meta.env.VITE_ZENDESK_STAGE_REPLICA === 'true';
}

/** Max JWT fetches per authenticated TruSpace user before we stop answering Messenger. */
const MAX_JWT_FETCHES_PER_USER = 3;

/** Minimum gap between JWT fetches for the same user (ms). */
const JWT_FETCH_COOLDOWN_MS = 2_000;

/** Whether the Zendesk widget script has been injected. */
let injected = false;

/** Whether `loginUser` has already been registered for the current identity. */
let loginRegistered = false;

/** TruSpace user id currently authenticated in Messenger (null = anonymous). */
let authenticatedUserId: number | null = null;

/** Successful + failed JWT fetch attempts for `authenticatedUserId`. */
let jwtFetchCount = 0;

/** Timestamp of the last JWT fetch start. */
let lastJwtFetchAt = 0;

/** In-flight JWT promise so concurrent Messenger callbacks share one request. */
let jwtInFlight: Promise<string> | null = null;

/** When true, further JWT fetches for this user are refused. */
let circuitOpen = false;

/** Stage-replica storm scheduler — mimics Messenger + RC11 cleanup retry loops. */
let stageReplicaStormTimer: number | null = null;
let stageReplicaStormActive = false;

/** The callback Zendesk hands the `loginUser` handler; invoked with the minted JWT. */
type ZendeskJwtCallback = (jwt: string) => void;
/** The `loginUser` handler Zendesk calls, passing it the JWT callback above. */
type ZendeskLoginCallback = (callback: ZendeskJwtCallback) => void;
type ZendeskJwtFetcher = () => Promise<string>;
type ZendeskLoginErrorCallback = (error: unknown) => void;

/**
 * Reset circuit-breaker counters (tests / after logout).
 */
function resetJwtCircuit(): void {
  jwtFetchCount = 0;
  lastJwtFetchAt = 0;
  jwtInFlight = null;
  circuitOpen = false;
  stageReplicaStormActive = false;
  if (stageReplicaStormTimer != null) {
    window.clearTimeout(stageReplicaStormTimer);
    stageReplicaStormTimer = null;
  }
}

/**
 * Stage QA only — after a failed JWT handoff, re-register `loginUser` on a short
 * interval so local dev reproduces Stage's chat-token request storm (production
 * has no React StrictMode double-mount cap). Stop with VITE_ZENDESK_STAGE_REPLICA=false.
 */
function scheduleStageReplicaStorm(fetchJwt: ZendeskJwtFetcher, userId: number): void {
  if (!isZendeskStageReplica() || stageReplicaStormActive) return;
  stageReplicaStormActive = true;

  const tick = (): void => {
    if (
      !isZendeskStageReplica() ||
      authenticatedUserId !== userId ||
      !stageReplicaStormActive
    ) {
      stageReplicaStormActive = false;
      stageReplicaStormTimer = null;
      return;
    }
    void fetchJwt().catch(() => {
      // Keep hammering — Stage saw 30k+ before the tab froze.
    });
    stageReplicaStormTimer = window.setTimeout(tick, 16);
  };

  stageReplicaStormTimer = window.setTimeout(tick, 16);
}

/**
 * True when the Zendesk snippet has loaded and exposed `window.zE`.
 */
function isZendeskApiReady(): boolean {
  return typeof window.zE === 'function' && Boolean(document.getElementById('ze-snippet'));
}

/**
 * Run a callback once the real Zendesk API is available.
 */
function whenZendeskReady(callback: () => void): void {
  if (isZendeskApiReady()) {
    callback();
    return;
  }

  const onLoad = (): void => {
    document.getElementById('ze-snippet')?.removeEventListener('load', onLoad);
    window.setTimeout(() => {
      if (isZendeskApiReady()) callback();
    }, 100);
  };
  document.getElementById('ze-snippet')?.addEventListener('load', onLoad);

  const started = Date.now();
  const POLL_MS = 200;
  const POLL_TIMEOUT_MS = 15_000;
  const timer = window.setInterval(() => {
    if (isZendeskApiReady()) {
      window.clearInterval(timer);
      document.getElementById('ze-snippet')?.removeEventListener('load', onLoad);
      callback();
      return;
    }
    if (Date.now() - started > POLL_TIMEOUT_MS) {
      window.clearInterval(timer);
    }
  }, POLL_MS);
}

/**
 * Inject the Zendesk Messenger snippet into the page.
 * No-ops if the key is missing or the script is already present.
 */
export function initZendesk(): void {
  if (!ZENDESK_KEY || injected) return;
  if (document.getElementById('ze-snippet')) {
    injected = true;
    return;
  }

  injected = true;
  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Fetch a chat JWT with single-flight, cooldown, and a hard attempt cap.
 *
 * @throws when the circuit is open or the underlying fetcher fails.
 */
async function fetchJwtGuarded(fetchJwt: ZendeskJwtFetcher): Promise<string> {
  if (circuitOpen || jwtFetchCount >= MAX_JWT_FETCHES_PER_USER) {
    circuitOpen = true;
    throw new Error('Zendesk chat JWT circuit open — Messenger login stopped for this session');
  }

  if (jwtInFlight) {
    return jwtInFlight;
  }

  const elapsed = Date.now() - lastJwtFetchAt;
  if (lastJwtFetchAt > 0 && elapsed < JWT_FETCH_COOLDOWN_MS) {
    throw new Error('Zendesk chat JWT cooldown — ignoring Messenger re-request');
  }

  jwtFetchCount += 1;
  lastJwtFetchAt = Date.now();

  jwtInFlight = fetchJwt()
    .then((jwt) => {
      if (!jwt || typeof jwt !== 'string') {
        circuitOpen = true;
        throw new Error('Zendesk chat JWT empty');
      }
      return jwt;
    })
    .catch((err: unknown) => {
      circuitOpen = true;
      throw err;
    })
    .finally(() => {
      jwtInFlight = null;
    });

  return jwtInFlight;
}

/**
 * Register Messenger `loginUser` so Zendesk can request a chat JWT.
 * Failures are swallowed so the rest of the app keeps working (US-2).
 *
 * If a different TruSpace user is already authenticated in Messenger,
 * logs them out first so conversation history does not leak.
 *
 * @param fetchJwt - Resolves to a chat JWT from `GET /api/support/zendesk/chat-token`.
 * @param userId - TruSpace user id being authenticated (for switch detection).
 */
export function loginZendeskUser(fetchJwt: ZendeskJwtFetcher, userId: number): void {
  whenZendeskReady(() => {
    if (
      !isZendeskStageReplica() &&
      loginRegistered &&
      authenticatedUserId === userId
    ) {
      return;
    }

    if (loginRegistered && authenticatedUserId !== userId) {
      logoutZendeskUserSync();
    }

    registerLoginUser(fetchJwt, userId);
  });
}

/**
 * Wire `zE('messenger', 'loginUser', …)` for the given TruSpace user.
 */
function registerLoginUser(fetchJwt: ZendeskJwtFetcher, userId: number): void {
  if (typeof window.zE !== 'function') return;
  loginRegistered = true;
  authenticatedUserId = userId;
  resetJwtCircuit();

  const onJwt: ZendeskLoginCallback = (callback) => {
    const run = isZendeskStageReplica()
      ? fetchJwt()
      : fetchJwtGuarded(fetchJwt);
    void run
      .then((jwt) => {
        callback(jwt);
      })
      .catch(() => {
        // Silent failure — Messenger stays anonymous; app continues.
        if (isZendeskStageReplica()) {
          scheduleStageReplicaStorm(fetchJwt, userId);
        }
      });
  };

  // Optional 4th-arg error callback is supported by newer Messenger builds.
  const onError: ZendeskLoginErrorCallback = () => {
    circuitOpen = true;
  };

  try {
    window.zE('messenger', 'loginUser', onJwt, onError);
  } catch {
    window.zE('messenger', 'loginUser', onJwt);
  }

  // Auth often re-surfaces the vendor launcher; keep it suppressed for the custom FAB.
  window.setTimeout(() => {
    try {
      window.zE?.('messenger', 'hide');
    } catch {
      // ignore
    }
  }, 750);
}

/**
 * Immediately clear Messenger auth (must already be ready).
 */
function logoutZendeskUserSync(): void {
  loginRegistered = false;
  authenticatedUserId = null;
  resetJwtCircuit();
  if (typeof window.zE !== 'function') return;
  try {
    window.zE('messenger', 'logoutUser');
  } catch {
    // Widget may not be fully booted — ignore.
  }
}

/**
 * Log out of Messenger so the next visitor does not inherit this conversation.
 * Safe to call when the widget is not loaded.
 */
export function logoutZendeskUser(): void {
  stageReplicaStormActive = false;
  if (stageReplicaStormTimer != null) {
    window.clearTimeout(stageReplicaStormTimer);
    stageReplicaStormTimer = null;
  }
  if (!injected && !document.getElementById('ze-snippet')) {
    loginRegistered = false;
    authenticatedUserId = null;
    resetJwtCircuit();
    return;
  }

  if (isZendeskApiReady()) {
    logoutZendeskUserSync();
    return;
  }

  whenZendeskReady(() => {
    logoutZendeskUserSync();
  });
}

/**
 * Whether a Zendesk snippet key is configured for this build.
 */
export function isZendeskKeyConfigured(): boolean {
  return Boolean(ZENDESK_KEY);
}

/**
 * Test-only: inspect circuit state.
 *
 * @returns Snapshot of breaker counters for unit tests.
 */
export function __zendeskCircuitStateForTests(): {
  jwtFetchCount: number;
  circuitOpen: boolean;
  loginRegistered: boolean;
  authenticatedUserId: number | null;
} {
  return {
    jwtFetchCount,
    circuitOpen,
    loginRegistered,
    authenticatedUserId,
  };
}

/**
 * Test-only: reset module state between cases.
 */
export function __resetZendeskForTests(): void {
  injected = false;
  loginRegistered = false;
  authenticatedUserId = null;
  resetJwtCircuit();
}

/** localStorage key for a user-pinned FAB position (viewport left/top px). */
export const ZENDESK_LAUNCHER_POS_KEY = 'truspace.zendeskLauncherPos.v2';

/** Legacy hide-preference key — cleared when the draggable FAB mounts. */
export const ZENDESK_MESSENGER_HIDDEN_KEY = 'truspace.zendeskMessengerHidden';

const LAUNCHER_SUPPRESS_STYLE_ID = 'ts-zendesk-launcher-suppress';

/** Skip hide-on-close while an intentional open is in flight (ms). */
const MESSENGER_OPEN_HIDE_GRACE_MS = 750;

/** `Date.now()` until which a Zendesk `close` event must not call `hide()`. */
let ignoreCloseHideUntil = 0;

/**
 * Mark the next close event as part of an open/reposition, not a user dismiss.
 */
function beginMessengerOpen(): void {
  ignoreCloseHideUntil = Date.now() + MESSENGER_OPEN_HIDE_GRACE_MS;
}

export type ZendeskLauncherPos = {
  left: number;
  top: number;
};

/**
 * Read the persisted custom-launcher position, or null if unset/invalid.
 */
export function getZendeskLauncherPosition(): ZendeskLauncherPos | null {
  try {
    const raw = window.localStorage.getItem(ZENDESK_LAUNCHER_POS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object'
      || parsed === null
      || typeof (parsed as ZendeskLauncherPos).left !== 'number'
      || typeof (parsed as ZendeskLauncherPos).top !== 'number'
      || !Number.isFinite((parsed as ZendeskLauncherPos).left)
      || !Number.isFinite((parsed as ZendeskLauncherPos).top)
    ) {
      return null;
    }
    return {
      left: (parsed as ZendeskLauncherPos).left,
      top: (parsed as ZendeskLauncherPos).top,
    };
  } catch {
    return null;
  }
}

/**
 * Persist the custom-launcher position.
 */
export function setZendeskLauncherPosition(pos: ZendeskLauncherPos): void {
  try {
    window.localStorage.setItem(ZENDESK_LAUNCHER_POS_KEY, JSON.stringify(pos));
  } catch {
    // Private mode / blocked storage — in-session position still works.
  }
}

/**
 * CSS fallback: zero out Zendesk's default launcher iframe so it never covers
 * pagination. The conversation iframe stays usable via {@link openZendeskMessenger}.
 */
export function ensureNativeLauncherSuppressed(): void {
  if (document.getElementById(LAUNCHER_SUPPRESS_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = LAUNCHER_SUPPRESS_STYLE_ID;
  style.textContent = `
    iframe[title="Button to launch messaging window"],
    iframe[title="Button to open messaging window"],
    iframe[title*="Button to launch messaging"],
    iframe[title*="Open messaging window"] {
      opacity: 0 !important;
      pointer-events: none !important;
      width: 0 !important;
      height: 0 !important;
      max-width: 0 !important;
      max-height: 0 !important;
      border: 0 !important;
      position: fixed !important;
      bottom: -9999px !important;
      right: -9999px !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Hide the vendor launcher/widget chrome after boot and whenever the user
 * closes the messaging panel (custom FAB remains the only entry point).
 */
export function wireNativeLauncherSuppression(): () => void {
  ensureNativeLauncherSuppressed();
  // Drop the old hide-preference so it cannot fight the new FAB.
  try {
    window.localStorage.removeItem(ZENDESK_MESSENGER_HIDDEN_KEY);
  } catch {
    // ignore
  }

  let unsubscribeClose: (() => void) | undefined;

  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      // Keep widget available for open(), but do not leave the default bubble up.
      window.zE('messenger', 'close');
      window.zE('messenger', 'hide');
      const maybeUnsub = window.zE('messenger:on', 'close', () => {
        // `messenger:set` customization can emit close while we are opening.
        // hide() after that swallows the panel so the FAB looks dead.
        if (Date.now() < ignoreCloseHideUntil) return;
        try {
          window.zE?.('messenger', 'hide');
        } catch {
          // ignore
        }
      });
      if (typeof maybeUnsub === 'function') {
        unsubscribeClose = maybeUnsub as () => void;
      }
    } catch {
      // Widget not fully booted — ignore.
    }
  });

  return () => {
    unsubscribeClose?.();
  };
}

/**
 * Map a custom FAB viewport position to Zendesk floating `position` offsets.
 * Vertical offset is from the bottom; horizontal from the nearer side edge.
 *
 * @param fab - FAB top-left in viewport px
 * @param fabSize - FAB diameter in px
 */
export function fabPosToMessengerPosition(
  fab: ZendeskLauncherPos,
  fabSize: number,
): {
  side: 'left' | 'right';
  offset: { web: { horizontal: number; vertical: number } };
} {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const fabCenterX = fab.left + fabSize / 2;
  const side: 'left' | 'right' = fabCenterX < viewportW / 2 ? 'left' : 'right';
  const horizontal =
    side === 'left'
      ? Math.max(8, Math.round(fab.left))
      : Math.max(8, Math.round(viewportW - (fab.left + fabSize)));
  // Anchor the panel just above the FAB (vertical = distance from bottom).
  const vertical = Math.max(8, Math.round(viewportH - fab.top));
  return {
    side,
    offset: { web: { horizontal, vertical } },
  };
}

/**
 * Reposition the open messenger panel near the custom FAB (edge + offset).
 * Safe to call while dragging — Zendesk accepts mid-session `position` updates.
 */
export function setZendeskMessengerPositionNearFab(
  fab: ZendeskLauncherPos,
  fabSize: number,
): void {
  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      const position = fabPosToMessengerPosition(fab, fabSize);
      window.zE('messenger:set', 'customization', { position });
    } catch {
      // ignore
    }
  });
}

/**
 * Apply floating messenger position for the current FAB location, then open.
 */
export function openZendeskMessengerNearFab(
  fab: ZendeskLauncherPos,
  fabSize: number,
): void {
  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      beginMessengerOpen();
      window.zE('messenger', 'show');
      window.zE('messenger', 'open');
      const position = fabPosToMessengerPosition(fab, fabSize);
      window.zE('messenger:set', 'customization', { position });
    } catch {
      // ignore
    }
  });
}

/**
 * Open the messaging panel from the custom FAB (default bottom-right anchor).
 * Uses show+open because hide() removes the widget from the page.
 */
export function openZendeskMessenger(): void {
  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      beginMessengerOpen();
      window.zE('messenger', 'show');
      window.zE('messenger', 'open');
    } catch {
      // ignore
    }
  });
}

/**
 * Close the messaging panel (does not remove the custom FAB).
 */
export function closeZendeskMessenger(): void {
  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      window.zE('messenger', 'close');
      // Leave `show` so a subsequent open is snappy; CSS still hides the vendor bubble.
      // Full hide is applied by the close listener in {@link wireNativeLauncherSuppression}.
    } catch {
      // ignore
    }
  });
}

/**
 * Subscribe to messenger open/close for custom-FAB toggle state.
 *
 * @returns Unsubscribe cleanup
 */
export function subscribeZendeskMessengerOpenState(
  onChange: (isOpen: boolean) => void,
): () => void {
  let unsubOpen: (() => void) | undefined;
  let unsubClose: (() => void) | undefined;

  whenZendeskReady(() => {
    if (typeof window.zE !== 'function') return;
    try {
      const openRet = window.zE('messenger:on', 'open', () => onChange(true));
      const closeRet = window.zE('messenger:on', 'close', () => onChange(false));
      if (typeof openRet === 'function') unsubOpen = openRet as () => void;
      if (typeof closeRet === 'function') unsubClose = closeRet as () => void;
    } catch {
      // ignore
    }
  });

  return () => {
    unsubOpen?.();
    unsubClose?.();
  };
}

declare global {
  interface Window {
    zE?: (...args: unknown[]) => unknown;
  }
}
